import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  FiFileText,
  FiCalendar,
  FiUser,
  FiClock,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import { axiosget } from "../../helpers/httprequest";
import { Badge } from "../../components/ui/Badge";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import EmptyState from "../../components/ui/EmptyState";
import moment from "../../helpers/moment";
import PrintAdmission from "./PrintAdmission";

const Admission = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["detailedApplications"],
    queryFn: async () => {
      const req = await axiosget("ms_applications");
      return req?.data;
    },
  });

  const _status = (data) => {
    switch (data) {
      case 0:
        return { text: "Pending", variant: "warning" };
      case 1:
        return { text: "Provisional Admission", variant: "success" };
      case 2:
        return { text: "Conditional Admission", variant: "info" };
      default:
        return { text: "Invalid option", variant: "neutral" };
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <>
      <PageHeader
        title="Admission Status"
        subtitle="Check your application status and print admission letters"
        icon={<FiCheckCircle className="w-5 h-5" />}
      />

      {isLoading && <Loader />}
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}

      {!isLoading && data && data?.type === undefined && data.length === 0 && (
        <EmptyState
          variant="no-data"
          title="No Applications Found"
          description="Your processed applications will appear here. Please check back later."
          icon={<FiFileText className="h-12 w-12" />}
        />
      )}

      {data && data?.type === undefined && data.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3 mb-6"
        >
          {data.map((v, i) => {
            const status = _status(v?.status);

            return (
              <motion.div
                key={`_admission_${i}`}
                variants={rowVariants}
                className={`bg-white rounded-xl border overflow-hidden ${
                  v?.status === 1 ? "border-success-200" : v?.status === 2 ? "border-info-200" : "border-neutral-200"
                }`}
              >
                {/* Main Row */}
                <div className="px-5 py-4">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                        v?.status > 0 ? "bg-success-50" : "bg-neutral-100"
                      }`}>
                        {v?.status > 0 ? (
                          <FiCheckCircle className="h-5 w-5 text-success-600" />
                        ) : (
                          <FiClock className="h-5 w-5 text-neutral-500" />
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-neutral-900 font-mono">
                          {v?.code}
                        </span>
                        <p className="text-xs text-neutral-400 mt-0.5">
                          {moment(v?.createdAt)} ago
                        </p>
                      </div>
                    </div>
                    <Badge variant={status.variant}>{status.text}</Badge>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FiFileText className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600 truncate">
                        {v?.programme?.code} :: {v?.programme?.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiUser className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600">{v?.type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiCalendar className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600">
                        {v?.session?.code} :: {v?.session?.name}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-100 flex items-center justify-end gap-2">
                  {v?.status > 0 ? (
                    <PrintAdmission v={v} />
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <FiInfo className="h-3.5 w-3.5" />
                      <span>Application is being reviewed</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </>
  );
};

export default Admission;
